import { uiText, getUiLocale } from '../../../../utils/uiTranslation';
import { systemMessage } from '../../../../utils/localizedSystemMessages';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ref, get, push, set } from 'firebase/database';
import { database } from '../../../../firebase';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD;

const REVIEW_TYPES = [
  'annual',
  'quarterly',
  'mid_year',
  'probation',
  'performance',
  'one_on_one',
  'other'
];

const REVIEW_TYPE_LABELS = {
  'annual': 'Annual',
  'quarterly': 'Quarterly',
  'mid_year': 'Mid Year',
  'probation': 'Probation',
  'performance': 'Performance',
  'one_on_one': 'One on One',
  'other': 'Other'
};

const ScheduleReviewModal = ({ employeeId, companyUID, employeeName, employeeEmail, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL } = useLanguage();
  const [reviewType, setReviewType] = useState('performance');
  const [reviewDate, setReviewDate] = useState('');
  const [reviewTime, setReviewTime] = useState('09:00');
  const [notes, setNotes] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [scheduleStatus, setScheduleStatus] = useState('');
  const safeEmployeeName = employeeName || t('drawerModals.common.unknownEmployee', 'Unknown Employee');

  // Load calendar connections from Firebase
  useEffect(() => {
    const loadCalendarConnections = async () => {
      try {
        const calendarRef = ref(database, `companies/${companyUID}/calendarIntegrations`);
        const snapshot = await get(calendarRef);

        if (snapshot.exists()) {
          const connections = Object.values(snapshot.val());
          setCalendarConnections(connections);
        } else {
          setCalendarConnections([]);
        }
      } catch (error) {
        console.error('Error loading calendar connections:', error);
        setCalendarConnections([]);
      }
    };

    if (companyUID) {
      loadCalendarConnections();
    }
  }, [companyUID]);

  const handleSchedule = async () => {
    if (!reviewDate) {
      alert(t('drawerModals.scheduleReview.errors.missingDate', 'Please select a review date'));
      return;
    }

    setScheduling(true);
    setScheduleStatus(`📅 ${t('drawerModals.scheduleReview.status.scheduling', 'Scheduling review...')}`);

    try {
      const reviewDateTime = `${reviewDate}T${reviewTime}:00`;
      const startDateTime = new Date(reviewDateTime);
      const endDateTime = new Date(startDateTime.getTime() + (60 * 60000)); // 60 minutes default

      // First, save the review to Firebase
      const response = await fetch(`${BACKEND_URL}/employee/schedule-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          reviewType,
          reviewDate: reviewDateTime,
          notes: notes.trim() || undefined,
          scheduledByUID: currentUser?.uid,
          scheduledByName: currentUser?.displayName || currentUser?.email || 'HR Team'
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to schedule review');
      }

      // If calendar is connected, create calendar event
      if (calendarConnections.length > 0) {
        const calendarConnection = calendarConnections.find(conn =>
          conn.provider === 'google' || conn.provider === 'outlook'
        );

        if (calendarConnection) {
          setScheduleStatus(`📅 ${t('drawerModals.scheduleReview.status.creatingEvent', 'Creating calendar event...')}`);

          const reviewTitle = uiText('Performance Review - {{name}}', { name: employeeName });
          const reviewDescription = uiText('Performance Review: {{type}}', { type: uiText(REVIEW_TYPE_LABELS[reviewType] || reviewType) }) + '\n\n' + (notes.trim() || uiText('No additional notes'));

          // Get employee email for attendee list
          const attendeeEmails = employeeEmail ? [employeeEmail] : [];

          const calendarRequest = {
            company_uid: companyUID,
            employee_id: employeeId,
            review_title: reviewTitle,
            duration_minutes: 60,
            employee_email: employeeEmail,
            employee_name: employeeName,
            start_time: startDateTime.toISOString(),
            end_time: endDateTime.toISOString(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            attendee_emails: attendeeEmails,
            description: reviewDescription,
            token_data: {
              provider: calendarConnection.provider,
              access_token: calendarConnection.access_token,
              refresh_token: calendarConnection.refresh_token,
              token_uri: calendarConnection.token_uri,
              client_id: calendarConnection.client_id,
              client_secret: calendarConnection.client_secret,
              scopes: calendarConnection.scopes
            }
          };

          const calendarResponse = await fetch(`${BACKEND_URL}/schedule-review-calendar`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(calendarRequest)
          });

          const calendarResult = await calendarResponse.json();
          if (calendarResult.success) {
            setScheduleStatus(`✅ ${t('drawerModals.scheduleReview.status.scheduled', 'Review scheduled with calendar invite!')}`);

            // Send Notification
            try {
              const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
              await set(notifRef, {
                ...systemMessage('review_scheduled', { type: REVIEW_TYPE_LABELS[reviewType] || reviewType, date: reviewDateTime, time: reviewTime }, getUiLocale()),
                type: 'info',
                timestamp: Date.now(),
                read: false
              });
            } catch (err) { console.error(err); }

            setTimeout(() => {
              onSuccess?.();
              onClose();
            }, 1000);
          } else {
            // Review is saved, but calendar event failed - still show success
            setScheduleStatus(uiText('✅ Review scheduled (calendar event failed)'));

            // Send Notification
            try {
              const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
              await set(notifRef, {
                ...systemMessage('review_scheduled', { type: REVIEW_TYPE_LABELS[reviewType] || reviewType, date: reviewDateTime, time: reviewTime }, getUiLocale()),
                type: 'info',
                timestamp: Date.now(),
                read: false
              });
            } catch (err) { console.error(err); }

            setTimeout(() => {
              onSuccess?.();
              onClose();
            }, 1000);
          }
        } else {
          // No calendar connection, just save the review
          setScheduleStatus(`✅ ${t('drawerModals.scheduleReview.status.scheduledPlain', 'Review scheduled successfully!')}`);

          // Send Notification
          try {
            const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
            await set(notifRef, {
              ...systemMessage('review_scheduled', { type: REVIEW_TYPE_LABELS[reviewType] || reviewType, date: reviewDateTime, time: reviewTime }, getUiLocale()),
              type: 'info',
              timestamp: Date.now(),
              read: false
            });
          } catch (err) { console.error(err); }

          setTimeout(() => {
            onSuccess?.();
            onClose();
          }, 1000);
        }
      } else {
        // No calendar connection, just save the review
        setScheduleStatus(`✅ ${t('drawerModals.scheduleReview.status.scheduledPlain', 'Review scheduled successfully!')}`);

        // Send Notification
        try {
          const notifRef = push(ref(database, `companies/${companyUID}/employees/${employeeId}/notifications`));
          await set(notifRef, {
            ...systemMessage('review_scheduled', { type: REVIEW_TYPE_LABELS[reviewType] || reviewType, date: reviewDateTime, time: reviewTime }, getUiLocale()),
            type: 'info',
            timestamp: Date.now(),
            read: false
          });
        } catch (err) { console.error(err); }

        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error('Schedule review failed:', error);
      setScheduleStatus(`❌ ${t('drawerModals.scheduleReview.status.failed', 'Failed to schedule review')}`);
      alert(t('drawerModals.scheduleReview.errors.failure', {
        message: error.message || t('drawerModals.scheduleReview.status.failed', 'Failed to schedule review')
      }));
      setScheduling(false);
    }
  };

  // Set default date to tomorrow
  React.useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setReviewDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '600px',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left'
        }}
      >
        <div className="modal-header">
          <h2>{t('drawerModals.scheduleReview.title', 'Schedule Performance Review')}</h2>
          <button className="close" onClick={onClose}>&times;</button>
        </div>
        <div className="form-section">
          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.employee', 'Employee')}
            </label>
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#F8F9FA',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              color: 'var(--app-heading, #031128)'
            }}>
              {safeEmployeeName}
            </div>
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.scheduleReview.reviewType', 'Review Type')}
            </label>
            <select
              value={reviewType}
              onChange={(e) => setReviewType(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'var(--app-heading, #031128)',
                backgroundColor: '#FFFFFF',
                cursor: 'pointer'
              }}
            >
              {REVIEW_TYPES.map(type => (
                <option key={type} value={type}>
                  {t(`drawerModals.scheduleReview.types.${type}`, REVIEW_TYPE_LABELS[type] || type)}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.common.date', 'Date')} <span style={{ color: 'var(--app-tone-eb4040, #EB4040)' }}>*</span>
              </label>
              <input
                type="date"
                value={reviewDate}
                onChange={(e) => setReviewDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  color: 'var(--app-heading, #031128)'
                }}
              />
            </div>

            <div className="input-group">
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--app-heading, #031128)'
              }}>
                {t('drawerModals.common.time', 'Time')}
              </label>
              <input
                type="time"
                value={reviewTime}
                onChange={(e) => setReviewTime(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E0E0E0',
                  borderRadius: '8px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  color: 'var(--app-heading, #031128)'
                }}
              />
            </div>
          </div>

          <div className="input-group">
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              fontWeight: 600,
              color: 'var(--app-heading, #031128)'
            }}>
              {t('drawerModals.common.notesOptional', 'Notes (Optional)')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('drawerModals.scheduleReview.notesPlaceholder', 'Add any notes about this review...')}
              style={{
                width: '100%',
                minHeight: '100px',
                padding: '12px 16px',
                border: '2px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                color: 'var(--app-heading, #031128)',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Schedule Status */}
          {scheduleStatus && (
            <div style={{
              marginTop: '20px',
              padding: '12px 16px',
              backgroundColor: scheduleStatus.includes('❌') ? '#FEF2F2' : '#F0FDF4',
              border: `1px solid ${scheduleStatus.includes('❌') ? '#EB4040' : '#40EB6E'}`,
              borderRadius: '8px',
              fontFamily: 'Montserrat',
              fontSize: '14px',
              color: scheduleStatus.includes('❌') ? 'var(--app-tone-eb4040, #EB4040)' : 'var(--app-heading, #031128)'
            }}>
              {scheduleStatus}
            </div>
          )}
        </div>
        {!scheduleStatus.includes('✅') && (
          <div className="modal-footer" style={{
            padding: '20px 32px',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            flexDirection: isRTL ? 'row-reverse' : 'row'
          }}>
            <button
              className="btn-secondary"
              onClick={onClose}
              disabled={scheduling}
              style={{
                padding: '12px 24px',
                backgroundColor: '#F8F9FA',
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: "var(--app-neutral-ink, #031128)",
                cursor: scheduling ? 'not-allowed' : 'pointer'
              }}
              data-light-control={"quiet"}>
              {t('common:buttons.cancel', 'Cancel')}
            </button>
            <button
              className="btn-primary"
              onClick={handleSchedule}
              disabled={scheduling || !reviewDate}
              style={{ '--light-control-fill': scheduling || !reviewDate ? '#CCCCCC' : '#568ded',
                padding: '12px 24px',
                backgroundColor: scheduling || !reviewDate ? '#CCCCCC' : '#568ded',
                border: 'none',
                borderRadius: '8px',
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: scheduling || !reviewDate ? 'not-allowed' : 'pointer'
              }}
              data-light-control={scheduling || !reviewDate ? "quiet" : "filled"}>
              {scheduling ? t('drawerModals.scheduleReview.cta.processing', 'Scheduling...') : t('drawerModals.scheduleReview.cta.primary', 'Schedule Review')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleReviewModal;
